import { Feather } from "@expo/vector-icons";
import React from "react";
import {
  ActivityIndicator,
  Pressable,
  PressableProps,
  Text,
} from "react-native";
import { useRsvpColors } from "../theme";

interface Props extends PressableProps {
  title: string;
  loading?: boolean;
  // Optional Feather icon rendered before the title (e.g. "users" on Join Waitlist)
  icon?: React.ComponentProps<typeof Feather>["name"];
}

// react-native-web adds `hovered` to the Pressable state; the react-native
// types only know `pressed` (native ignores hover, which is fine).
interface PressableState {
  pressed: boolean;
  hovered?: boolean;
}

/**
 * Full-width primary action button (min 44px tap target): secondary (accent)
 * background with primary-color text, inverting the two on hover.
 */
const RsvpPrimaryButton: React.FC<Props> = ({
  title,
  loading,
  icon,
  disabled,
  style,
  ...rest
}) => {
  const colors = useRsvpColors();
  const isDisabled = disabled || loading;
  return (
    <Pressable
      accessibilityRole="button"
      disabled={isDisabled}
      style={(state) => {
        const { pressed, hovered } = state as PressableState;
        const inverted = hovered && !isDisabled;
        return [
          {
            backgroundColor: inverted ? colors.background : colors.accent,
            borderWidth: 1,
            borderColor: colors.accent,
            borderRadius: 12,
            paddingVertical: 16,
            paddingHorizontal: 24,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            minHeight: 56,
            opacity: isDisabled ? 0.6 : pressed ? 0.9 : 1,
            transform: [{ scale: pressed && !isDisabled ? 0.98 : 1 }],
          },
          style as any,
        ];
      }}
      {...rest}
    >
      {(state) => {
        const { hovered } = state as PressableState;
        const contentColor =
          hovered && !isDisabled ? colors.accent : colors.background;
        return loading ? (
          <ActivityIndicator color={contentColor} />
        ) : (
          <>
            {icon ? (
              <Feather name={icon} size={20} color={contentColor} />
            ) : null}
            <Text
              style={{
                color: contentColor,
                fontSize: 18,
                fontWeight: "700",
              }}
            >
              {title}
            </Text>
          </>
        );
      }}
    </Pressable>
  );
};

export default RsvpPrimaryButton;
